import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, Phone, Sparkles, Volume2, Headphones, AlertTriangle, RefreshCw, ChevronRight, Square, Play, Bot } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { KpiCard } from '@/components/shared/KpiCard';
import { StatusChip } from '@/components/shared/StatusChip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AGENTS } from '@/data/seed';
import { useApp } from '@/context/AppContext';
import { playAudio, stopSpeaking, warmVoices, isVoiceSupported } from '@/lib/voice';
import { SUPERVISOR_CALLS } from '@/data/voiceLines';
import { usePageTitle } from '@/lib/usePageTitle';
import { useConfirm } from '@/components/shared/ConfirmDialog';
import { cn } from '@/lib/utils';

const LIVE_CALLS = SUPERVISOR_CALLS;

export default function SupervisorCockpit() {
  usePageTitle('Supervisor Cockpit');
  const { pushEvent } = useApp();
  const confirm = useConfirm();
  const [listening, setListening] = useState(null); // active call object
  const [playingIdx, setPlayingIdx] = useState(-1);
  const [feedUpdatedAt, setFeedUpdatedAt] = useState(Date.now());

  useEffect(() => () => stopSpeaking(), []);
  // Tick the live-feed timestamp every second.
  useEffect(() => {
    const t = setInterval(() => setFeedUpdatedAt(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const rebalance = async () => {
    const ok = await confirm({
      title: 'Run auto-rebalance?',
      description: 'The engine will redistribute case load across the team to even out capacity. Affected agents will receive a notification with the new queue.',
      impactSummary: [
        { label: 'Cases moved', value: '14' },
        { label: 'From', value: 'Sara Al-Qahtani (42/45)' },
        { label: 'To', value: 'Mohammed Al-Otaibi (28/40)' },
        { label: 'Reversible', value: 'Yes — undo within 1 hour' },
      ],
      confirmLabel: 'Rebalance now',
      tone: 'warning',
    });
    if (!ok) return;
    toast.success('Auto-rebalance proposal generated', { description: 'Move 14 cases from Sara Al-Qahtani → Mohammed Al-Otaibi' });
    pushEvent({ type: 'AutoRebalanceProposed', src: 'Assignment Engine', dst: 'Supervisor Cockpit', status: 'success', payload: '14 cases proposed' });
  };

  const decideException = async (row, decision) => {
    const destructive = decision === 'reject';
    const ok = await confirm({
      title: `${decision === 'approve' ? 'Approve' : 'Reject'} exception request?`,
      description: `${row.agent} requested: ${row.ask}`,
      impactSummary: [
        { label: 'Case', value: row.case },
        { label: 'Decision', value: decision === 'approve' ? 'Approve' : 'Reject' },
        { label: 'Logged to audit', value: 'Yes' },
      ],
      confirmLabel: decision === 'approve' ? 'Approve' : 'Reject',
      tone: destructive ? 'destructive' : 'warning',
    });
    if (!ok) return;
    toast[decision === 'approve' ? 'success' : 'message'](`${row.case} ${decision === 'approve' ? 'approved' : 'rejected'}`);
    pushEvent({ type: 'ExceptionDecision', src: 'Supervisor Cockpit', dst: 'Audit', status: 'success', payload: `${row.case} ${decision}`, caseId: row.case });
  };

  const startListen = async (call) => {
    warmVoices();
    setListening(call);
    pushEvent({ type: 'SupervisorListenStarted', src: 'Supervisor Cockpit', dst: 'Telephony', status: 'success', payload: `monitoring ${call.case}`, caseId: call.case });
    for (let i = 0; i < call.lines.length; i++) {
      const line = call.lines[i];
      setPlayingIdx(i);
      await playAudio(line.id);
      await new Promise((r) => setTimeout(r, 300));
    }
    setPlayingIdx(-1);
  };

  const stopListen = () => {
    stopSpeaking();
    setPlayingIdx(-1);
    setListening(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Supervisor cockpit"
        title="Team performance & live operations"
        subtitle="Live calls, SLA risks, QA flags, capacity rebalancing and exception approvals — all in one cockpit."
        actions={(
          <>
            <Button variant="outline" size="sm"><AlertTriangle className="h-4 w-4 mr-1.5" />SLA breaches</Button>
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={rebalance} data-testid="auto-rebalance">
              <RefreshCw className="h-4 w-4 mr-1.5" aria-hidden="true" />Auto-rebalance
            </Button>
          </>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Cases per agent" value={36} format="num" trend={1.2} hint="Across team" icon={Users} />
        <KpiCard label="Unworked > 24h" value={42} format="num" trend={-8} hint="backlog" icon={AlertTriangle} />
        <KpiCard label="Callbacks due today" value={28} format="num" trend={4} icon={Phone} />
        <KpiCard label="Broken promises" value={11} format="num" trend={-2} />
        <KpiCard label="Avg QA score" value={92.4} format="pct" trend={1.6} hint="team avg" />
      </div>

      {/* Live calls + QA + Productivity */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="surface-card lg:col-span-2 overflow-hidden">
          <div className="p-5 flex items-center justify-between border-b border-border">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">Live calls</p>
              <h3 className="text-base font-semibold mt-0.5">In-progress conversations</h3>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="relative inline-flex h-2 w-2"><span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" /></span>
              <span className="text-[11px] text-destructive font-medium">{LIVE_CALLS.length} active</span>
            </div>
          </div>
          <div className="divide-y divide-border">
            {LIVE_CALLS.map((c, i) => (
              <div key={i} className="px-5 py-3 grid grid-cols-12 items-center text-sm hover:bg-muted/40">
                <div className="col-span-4 flex items-center gap-2.5">
                  <div className="h-7 w-7 rounded-full bg-primary/15 text-gold-deep flex items-center justify-center text-[11px] font-semibold">{c.agent.split(' ').map((w) => w[0]).join('')}</div>
                  <div className="leading-tight">
                    <p className="font-medium">{c.agent}</p>
                    <p className="text-[11px] text-muted-foreground">{c.case} • debtor {c.debtor} <span className="ml-1 text-primary">{c.dialect} dialect</span></p>
                  </div>
                </div>
                <div className="col-span-3 text-muted-foreground text-xs"><Headphones className="h-3.5 w-3.5 inline mr-1" />{c.state}</div>
                <div className="col-span-2 font-mono-num text-sm">{c.dur}</div>
                <div className="col-span-2">
                  <Badge variant="outline" className={c.qa === 'OK' ? 'bg-success/10 text-success border-success/40' : 'bg-warning/10 text-warning border-warning/40'}>{c.qa === 'OK' ? 'QA OK' : 'Watch'}</Badge>
                </div>
                <div className="col-span-1 text-right">
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => startListen(c)}><Volume2 className="h-3.5 w-3.5 mr-1" />Listen</Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="surface-card p-5">
          <p className="text-[10px] uppercase tracking-widest text-primary">Agent productivity</p>
          <h3 className="text-base font-semibold mt-0.5 mb-3">Calls today</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={AGENTS} layout="vertical" margin={{ left: 0, right: 8 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 4" horizontal={false} />
                <XAxis type="number" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis type="category" dataKey="name" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={10} width={92} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="todayCalls" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Exception approvals */}
      <div className="surface-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Pending exception approvals</p>
            <h3 className="text-base font-semibold mt-0.5">Awaiting your decision</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">3 items</Badge>
        </div>
        <div className="space-y-2">
          {[
            { case: 'CASE-10428', agent: 'Layla Bin Ali', ask: 'Discount 12% on SAR 24,300 (above 10% threshold)', tone: 'warning' },
            { case: 'CASE-10416', agent: 'Fares Sayer', ask: 'Reduce contact window to 09:00–18:00 for high-value debtor', tone: 'info' },
            { case: 'CASE-10433', agent: 'Sara Al-Qahtani', ask: 'Skip-trace approval for CASE-10433 (out-of-date contact)', tone: 'warning' },
          ].map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-muted/40 hover:bg-muted">
              <div className="flex items-center gap-3">
                <div className={`h-8 w-8 rounded-md flex items-center justify-center ${r.tone === 'warning' ? 'bg-warning/15 text-warning' : 'bg-info/15 text-info'}`}><AlertTriangle className="h-4 w-4" aria-hidden="true" /></div>
                <div>
                  <p className="text-sm font-medium">{r.case} • {r.agent}</p>
                  <p className="text-xs text-muted-foreground">{r.ask}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => decideException(r, 'reject')} data-testid={`exception-reject-${r.case}`}>Reject</Button>
                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => decideException(r, 'approve')} data-testid={`exception-approve-${r.case}`}>Approve</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Listen dialog */}
      <Dialog open={!!listening} onOpenChange={(o) => !o && stopListen()}>
        <DialogContent className="max-w-2xl">
          {listening && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Headphones className="h-5 w-5 text-primary" />
                  Silent monitor — {listening.case}
                  <Badge variant="outline" className="ml-2 text-[10px] bg-destructive/10 text-destructive border-destructive/30">
                    <span className="h-1.5 w-1.5 rounded-full bg-destructive mr-1 animate-pulse" />
                    LIVE
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  Listening to recorded call audio. Debtor and agent voices are AI-generated based on nationality and gender.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="p-3 rounded-lg border border-border bg-muted/40">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Agent</p>
                  <p className="text-sm font-medium mt-0.5">{listening.agent}</p>
                  <p className="text-[11px] text-primary mt-0.5">{listening.agentVoice.label} · {listening.agentVoice.name}</p>
                </div>
                <div className="p-3 rounded-lg border border-border bg-muted/40">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Debtor</p>
                  <p className="text-sm font-medium mt-0.5">{listening.debtor}</p>
                  <p className="text-[11px] text-primary mt-0.5">{listening.debtorVoice.label} · {listening.debtorVoice.name}</p>
                </div>
              </div>

              <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin pr-1">
                {listening.lines.map((l, i) => (
                  <div
                    key={i}
                    className={cn(
                      'flex gap-2 text-sm transition-all',
                      l.who === 'debtor' ? 'flex-row-reverse' : '',
                      playingIdx === i ? 'opacity-100' : i < playingIdx || playingIdx === -1 ? 'opacity-90' : 'opacity-40'
                    )}
                  >
                    <span className={cn('mt-1 inline-flex h-5 px-1.5 items-center text-[10px] rounded font-semibold uppercase', l.who === 'agent' ? 'bg-primary/15 text-gold-deep' : 'bg-muted text-muted-foreground')}>{l.who}</span>
                    <p className={cn(
                      'max-w-[80%] px-3 py-1.5 rounded-lg text-sm border',
                      l.who === 'agent' ? 'bg-card border-border' : 'bg-primary/10 border-primary/30',
                      playingIdx === i && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                    )}>{l.text}</p>
                  </div>
                ))}
              </div>

              {!isVoiceSupported() && (
                <p className="text-[11px] text-warning">Your browser does not support audio playback — transcript is shown silently.</p>
              )}

              <div className="flex items-center justify-between border-t border-border pt-3">
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <Bot className="h-3.5 w-3.5 text-primary" />
                  AI voice playback • encrypted recording on file
                </div>
                <Button size="sm" onClick={stopListen} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  <Square className="h-3.5 w-3.5 mr-1.5" />Stop monitoring
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
